import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SubjectOutlinedIcon from "@mui/icons-material/SubjectOutlined";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { CustomizedTable } from "../../../components/mui/table/custom-table";
import { DATE_FORMATS, formatDate } from "../../../services/date-and-time.utils";
import {
  formatTargetCompletion,
  getInitiativeTypeDisplayLabel,
} from "../../ambassadors/ambassador-details/initiatives/utils";
import { INITIATIVE_PROJECT_COLUMNS } from "./table-config";

function InitiativesExpandedRowData({
  projects = [],
  onEditClick,
  onDeleteClick,
  onSummaryClick,
}: any) {
  const handleTDRenderer = (row, column) => {
    switch (column.rendererType) {
      case "TYPE": {
        const toolNames =
          row.type?.toLowerCase() === "replace tool" && row.toolName
            ? String(row.toolName)
                .split(",")
                .map((tool) => tool.trim())
                .filter(Boolean)
            : [];

        return (
          <Box>
            <Typography fontSize={13}>
              {getInitiativeTypeDisplayLabel(row.type)}
            </Typography>
            {toolNames.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.75}>
                {toolNames.map((tool) => (
                  <Chip
                    key={tool}
                    size="small"
                    label={tool}
                    sx={{
                      height: 22,
                      fontSize: 11,
                      fontWeight: 600,
                      bgcolor: "#F3F4F6",
                      color: "#374151",
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        );
      }
      case "ACTIVE":
        return (
          <Chip
            size="small"
            label={row.isActive ? "Active" : "Inactive"}
            sx={{
              height: 24,
              fontSize: 12,
              fontWeight: 600,
              bgcolor: row.isActive ? "#ECFDF5" : "#F3F4F6",
              color: row.isActive ? "#059669" : "#6B7280",
            }}
          />
        );
      case "VISIBILITY":
        return (
          <Chip
            size="small"
            label={row.isPublic ? "Public" : "Private"}
            sx={{
              height: 24,
              fontSize: 12,
              fontWeight: 600,
              bgcolor: row.isPublic ? "#EFF6FF" : "#FFFBEB",
              color: row.isPublic ? "#2563EB" : "#D97706",
            }}
          />
        );
      case "PLANNED_DELIVERY":
        return formatTargetCompletion(row.plannedDeliveryDate);
      case "UPDATED_AT":
        return row.updatedAt
          ? formatDate(row.updatedAt, DATE_FORMATS.fullDateWithTime)
          : "-";
      case "ACTION":
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={0.5}
          >
            <Tooltip title="View summary">
              <IconButton
                size="small"
                onClick={() => onSummaryClick?.(row)}
                aria-label="View summary"
              >
                <SubjectOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                aria-label="Edit"
                onClick={() => onEditClick?.(row)}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                aria-label="Delete"
                onClick={() => onDeleteClick?.(row)}
                sx={{ color: "#DC2626" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      default:
        return row?.[column.field] || "-";
    }
  };

  if (!projects.length) {
    return (
      <Box py={2} px={2}>
        <Typography fontSize={13} color="#6B7280">
          No initiatives for this ambassador.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1, mb: 2, ml: 4, mr: 1 }}>
      <CustomizedTable
        tdRenderer={handleTDRenderer}
        rows={projects}
        columns={INITIATIVE_PROJECT_COLUMNS}
      />
    </Box>
  );
}

export { InitiativesExpandedRowData };
